import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Grid, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { sourceModelInitial } from '../../../../Models/SourceModel';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { theme } from '../../../../theme';
import { PositionModel } from '../../../../Models/PositionModel';
import { OriginModel } from '../../../../Models/OriginModel';
import OriginService from '../../../../Services/OriginService';
import PositionService from '../../../../Services/PositionService';
import { ClientContext } from '../../ClientContext';
import { EditAction } from '../../../../shared/enum/EditAction';
import { HomeContext } from '../../../Home/HomeContext';
import { PageLocation } from '../../../../shared/enum/PageLocation';
import { IShowDialogProps } from '../../components/ClientPages';
import { ClientSourceContext } from '../ClientSourceContext';


export function ClientSourceModal({showDialog, setShowDialog}: IShowDialogProps) {
    const homeContext = React.useContext(HomeContext); 
    const clientContext = React.useContext(ClientContext); 
    const clientSourceContext = React.useContext(ClientSourceContext); 

    const [positions, setPositions] = React.useState<PositionModel[]>([]);
    const [origins, setOrigins] = React.useState<OriginModel[]>([]);

    const [nameNotDefinedError, setNameNotDefinedError] = React.useState(false);

    React.useEffect(() => {
        let clientId = clientContext.client.id;
        
        if (homeContext.pageLocation != PageLocation.CLIENT_EDIT)
            clientId = 0;

        fetchPositions(clientId);
        fetchOrigins(clientId);
    }, []);
    
    const fetchPositions = (clientId: number) => {
        if (positions.length == 0)
            PositionService.getAllByClientId(clientId)
                .then((res: AxiosResponse) => { setPositions(res.data); })
                .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Cargo. <br/>', error); });
    };
    const fetchOrigins = (clientId: number) => {
        if (origins.length == 0)
            OriginService.getAllByClientId(clientId)
                .then((res: AxiosResponse) => { setOrigins(res.data); })
                .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Procedência. <br/>', error); });
    };

    const handleModalSaveButton = () => {
        if (!hasError()) {
            const sources = [ ...clientContext.sources ];
            if (clientSourceContext.isInEditMode) {
                sources[clientSourceContext.actionFieldIndex] = clientSourceContext.currentSource;
                if (sources[clientSourceContext.actionFieldIndex].editAction != EditAction.INSERT)
                    sources[clientSourceContext.actionFieldIndex].editAction = EditAction.UPDATE;
                clientContext.setSources(sources);
                clientSourceContext.setIsInEditMode(false);
            }
            else 
                clientContext.setSources([...sources, { ...clientSourceContext.currentSource, editAction: EditAction.INSERT}]);
            
            clearAndCloseSourceModal();
        }
    };

    const handleModalDiscardButton = () => {
        clearAndCloseSourceModal();
    };

    const clearAndCloseSourceModal = () => {
        setShowDialog(false);
        clientSourceContext.setCurrentSource(sourceModelInitial);
        clientSourceContext.setActionFieldIndex(-1);
        clientSourceContext.setIsInEditMode(false);
        setNameNotDefinedError(false);
    };

    const hasError = () => {
        const nameNotDefinedError = !clientSourceContext.currentSource.name;

        setNameNotDefinedError(nameNotDefinedError);

        return nameNotDefinedError;
    };
    
    const sourceForm = () => {
        return (
            <Grid container spacing={3}>
                {getModalSourceName()}
                {getModalPositions()}
                {getModalOrigins()}
            </Grid>
        );
    };

    const getModalSourceName = () => {
        return (
            <Grid item xs={4}>
                <TextField
                    id="name"
                    value={clientSourceContext.currentSource.name}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => 
                        clientSourceContext.setCurrentSource({ ...clientSourceContext.currentSource, name: event.target.value })}
                    fullWidth
                    label="Nome da fonte"
                    variant="outlined"
                    inputProps={{ maxLength: 300 }}
                    error={nameNotDefinedError && !clientSourceContext.currentSource.name}
                    helperText={nameNotDefinedError && !clientSourceContext.currentSource.name ? 'Campo obrigatório' : ''} />
            </Grid>
        );
    };
    
    const getModalPositions = () => {
        return (
            <Grid item xs={4}>
                <Autocomplete
                    id="position"
                    options={positions}
                    value={clientSourceContext.currentSource.position}
                    onChange={(_, newValue: PositionModel | null) => 
                        clientSourceContext.setCurrentSource({ ...clientSourceContext.currentSource, position: newValue })}
                    getOptionLabel={(option) => option.description}
                    fullWidth
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Cargo"
                            variant="outlined" />
                    } />
            </Grid>
        );
    };

    const getModalOrigins = () => {
        return (
            <Grid item xs={4}>
                <Autocomplete
                    id="origin"
                    options={origins}
                    value={clientSourceContext.currentSource.origin}
                    onChange={(_, newValue: OriginModel | null) => 
                        clientSourceContext.setCurrentSource({ ...clientSourceContext.currentSource, origin: newValue })}
                    getOptionLabel={(option) => option.name}
                    fullWidth
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Procedência"
                            variant="outlined" />
                    } />
            </Grid>
        );
    };

    return (
        <BasicConfirmDialog
            title={clientSourceContext.isInEditMode ? 'Editar fonte' : 'Adicionar fonte'}
            description={clientSourceContext.isInEditMode
                ? 'Para editar uma fonte, altere os campos que deseja e clique em salvar.'
                : 'Para adicionar uma nova fonte, insira os dados solicitados e clique em salvar.'
            }
            textFirstButton="Descartar"
            handleFirstButton={handleModalDiscardButton}
            textSecondButton={clientSourceContext.isInEditMode ? 'Salvar Alterações' : 'Salvar Fonte'}
            handleSecondButton={handleModalSaveButton}
            open={showDialog}
            children={sourceForm()}
            cssProperties={{ 
                minWidth: '800px',
                maxWidth: '800px',
                padding: '1rem',
                colorFirstButton: theme.palette.primary.main,
                justifyButtons: 'flex-end',
                titleVariant: 'h4',
                descriptionVariant: 'body1'
            }} /> 
    );
}